///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GtthAlertService } from '../../../services/alert.service';
import { GtthHistoryService } from '../../../project-view-panel/logical-topology/engine/history-service';
import { UserAuthorities } from '../../../services/user/user-authorities';
import { finalize, Observable, Subject } from 'rxjs';
import { ProjectService } from '../../../services/project/project.service';
import { Principal } from '../../../shared/auth/principal.service';
import { GtthUpdateMainConfigCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/update-main-config-command';
import { ValidationAlertModel, ValidationAlertType } from '../../../shared/directives/validation-directives/validation-alert.model';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { GtthProjectStateService } from '../../../project-view-panel/logical-topology/engine/project-state.service';
import { EngineProcessTypeEnum, ProjectDTO, ProjectFolderDTO, ProjectFolderEnum } from '../../../generated/shared-types.model';
import { FoldersTreeService, FolderTreeNode } from '../../folders-tree/folders-tree.service';
import { FoldersService, RootFoldersInfo } from '../../../services/project/folders.service';
import { takeUntil } from 'rxjs/operators';
import { isDefined } from '@siklu/sui-lib/utils';
import { GtthSharedCommonModule } from '../../../shared/shared-common.module';
import { SuiLoadingDirective, SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { FoldersTreeComponent } from '../../folders-tree/folders-tree.component';
import { GtthSharePipesModule } from '../../../shared/shared-pipes.module';
import { GtthEntityNameValidationDirective } from '../../../shared/directives/validation-directives/entity-name.directive';

@Component({
    selector: 'winde-project-gtth-clone-dialog',
    templateUrl: './project-gtth-clone-dialog.component.html',
    styleUrls: ['../../projects.scss'],
    providers: [FoldersTreeService],
    imports: [
        MatDialogModule,
        GtthSharedCommonModule,
        GtthSharePipesModule,
        SuiLoadingDirective,
        SuiTranslatePipe,
        SuiSvgIconComponent,
        FoldersTreeComponent,
        GtthEntityNameValidationDirective
    ]
})
export class ProjectGtthCloneDialogComponent implements OnInit, OnDestroy {
    public isSaveStarted = false;
    public isBomConfigProject = false;

    public project: ProjectDTO;
    public newName: string;
    public hasToRedirect = true;
    public showFormValidationAlert: boolean;
    public alertMessageSubject: Subject<ValidationAlertModel>;

    public tree: FolderTreeNode[];
    public rootFolderIds: RootFoldersInfo;

    public loaderKey = LoaderIds.PROJECT_CLONE_DIALOG_PRELOADER_ID;

    private parentFolderId: number;

    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly principal: Principal,
        private readonly alertService: GtthAlertService,
        private readonly historyService: GtthHistoryService,
        private readonly projectService: ProjectService,
        private readonly projectFolderService: FoldersService,
        private readonly foldersTreeService: FoldersTreeService,
        public readonly preloaderService: PreloaderService,
        public readonly projectStateService: GtthProjectStateService,
        public dialogRef: MatDialogRef<ProjectGtthCloneDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { project: ProjectDTO }
    ) {
        this.alertMessageSubject = new Subject();
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.confirmClone(this.project.id);
        }
    }

    ngOnInit() {
        this.preloaderService.show(this.loaderKey);
        this.project = this.data.project;
        this.parentFolderId = this.project.parentId;
        this.isSaveStarted = false;
        this.isBomConfigProject = this.project.engineType === EngineProcessTypeEnum.BOM_CONFIGURATOR;

        this.principal
            .hasAnyAuthority([UserAuthorities.SIKLU_ACCOUNTANT, UserAuthorities.SIKLU_CUSTOMER_SUPPORT, UserAuthorities.SIKLU_SALES])
            .then((hasAuthority: boolean) => {
                const customerId = hasAuthority ? this.project.customerId : undefined;
                this.loadFolders(customerId);
            });

        this.foldersTreeService.selectionChanges$.pipe(takeUntil(this.destroy$)).subscribe((folderId: number) => {
            this.parentFolderId = folderId;
        });
    }

    confirmClone(id: number) {
        if (!this.newName) {
            this.alertMessageSubject.next({ text: 'error.formRequiredFields' });
            return;
        }
        this.newName = this.newName.trim();

        if (!isDefined(this.parentFolderId)) {
            this.parentFolderId = this.isBomConfigProject
                ? this.rootFolderIds[ProjectFolderEnum.BOM_CONFIG]
                : this.rootFolderIds[ProjectFolderEnum.PROJECT];
        }

        if (this.project.name === this.newName && this.project.parentId === this.parentFolderId) {
            const parentFolder = this.foldersTreeService.getFolder(this.parentFolderId);
            if (parentFolder?.name) {
                this.alertMessageSubject.next({
                    text: 'error.projectWithSameNameAndLocation',
                    params: { name: this.newName, folder: parentFolder.name },
                    type: ValidationAlertType.WARNING
                });
                return;
            }
        }
        this.alertMessageSubject.next({ text: '' });

        if (!this.isSaveStarted) {
            this.preloaderService.show(this.loaderKey);

            this.isSaveStarted = true;

            this.historyService.projectUserSessionService.refreshSelectionAndOpenWindows(this.historyService);
            this.historyService.doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand)).then(() => {
                let cloneRequest: Observable<any>;
                if (this.projectStateService.isPotentiallyCorruptedProject()) {
                    cloneRequest = this.projectService.migrateProject(id, this.parentFolderId, this.newName);
                } else {
                    cloneRequest = this.projectService.cloneProject(id, this.parentFolderId, this.newName);
                }

                cloneRequest.pipe(finalize(() => this.preloaderService.hide(this.loaderKey))).subscribe({
                    next: (cloneId: number) => {
                        this.isSaveStarted = false;

                        if (cloneId) {
                            if (this.hasToRedirect) {
                                this.router.navigate(['/project-view', cloneId]).then(() => {
                                    this.dialogRef.close();
                                });
                            } else {
                                this.dialogRef.close();
                            }
                        }
                    },
                    error: (res: Response) => this.onSaveError(res)
                });
            });
        }
    }

    ngOnDestroy(): void {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadFolders(customerId?: number) {
        this.projectFolderService
            .getFolders(customerId, this.isBomConfigProject ? ProjectFolderEnum.BOM_CONFIG : ProjectFolderEnum.PROJECT)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((folders: ProjectFolderDTO[]) => {
                this.foldersTreeService.createTree(folders);
                this.rootFolderIds = this.projectFolderService.getRootFolderIds(customerId);
                if (
                    !isDefined(this.project.parentId) ||
                    this.project.parentId === this.rootFolderIds[ProjectFolderEnum.BOM_CONFIG] ||
                    this.project.parentId === this.rootFolderIds[ProjectFolderEnum.PROJECT]
                ) {
                    this.foldersTreeService.selectRootFolder();
                } else {
                    this.foldersTreeService.selectFolder(this.project.parentId);
                }
                this.changeDetector.markForCheck();
            });
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save - ${exception}`);
        }
        this.isSaveStarted = false;
        this.alertService.error(error.message, null, null);
    }
}

@Component({
    selector: 'winde-project-gtth-clone-popup',
    template: ''
})
export class ProjectGtthClonePopupComponent implements OnInit {
    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly projectService: ProjectService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectService.getProject(id).subscribe((project: ProjectDTO) => {
                if (!project) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(ProjectGtthCloneDialogComponent, {
                        width: '800px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            project
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        }
    }
}
